import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Starts a Stripe checkout for the given credit amount. */
export const useCheckout = (amountNum, amountValid) => {
  const [starting, setStarting] = useState(false);

  const startCheckout = async () => {
    if (!amountValid) {
      toast.error("Enter an amount between $5.00 and $500.00");
      return;
    }
    setStarting(true);
    try {
      const { data } = await api.post("/credits/checkout", {
        amount: Math.round(amountNum * 100) / 100,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setStarting(false);
    }
  };

  return { starting, startCheckout };
};

const validateAutoRefill = (enabled, threshold, refillAmount) => {
  if (enabled && (Number.isNaN(threshold) || threshold < 0 || threshold > 100)) {
    return "Trigger level must be between $0.00 and $100.00";
  }
  if (enabled && (Number.isNaN(refillAmount) || refillAmount < 5 || refillAmount > 500)) {
    return "Refill amount must be between $5.00 and $500.00";
  }
  return null;
};

/** Persists auto-refill settings; `credits` is the useCreditsData() bundle. */
export const useAutoRefillSave = (credits, onSaved) => {
  const [arSaving, setArSaving] = useState(false);

  const saveAutoRefill = async () => {
    const th = parseFloat(credits.arThreshold);
    const ra = parseFloat(credits.arAmount);
    const problem = validateAutoRefill(credits.arEnabled, th, ra);
    if (problem) {
      toast.error(problem);
      return;
    }
    setArSaving(true);
    try {
      const { data } = await api.put("/credits/auto-refill", {
        enabled: credits.arEnabled,
        threshold: Number.isNaN(th) ? 0.5 : th,
        refill_amount: Number.isNaN(ra) ? 10 : ra,
      });
      if (data.refill) {
        (data.refill.ok ? toast.success : toast.warning)(data.refill.message);
      } else {
        toast.success("Auto-refill settings saved");
      }
      onSaved();
    } catch {
      toast.error("Could not save auto-refill settings");
    } finally {
      setArSaving(false);
    }
  };

  return { arSaving, saveAutoRefill };
};
